import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { useEffect, useRef, useState } from "react";
import { breadcrumbSchema, faqSchema, personSchema, webPageSchema } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EducationalDisclaimer } from "@/components/EducationalDisclaimer";
import { withNetworkUtm } from "@/lib/network";
import { NetworkNextStep } from "@/components/NetworkNextStep";

import { EMAILS, emailForInquiry } from "@/lib/emails";

export default function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const successHeadingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (isSubmitted && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [isSubmitted]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const firstName = String(fd.get("firstName") || "").trim();
    const lastName = String(fd.get("lastName") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const inquiryType = String(fd.get("inquiryType") || "").trim();
    const message = String(fd.get("message") || "").trim();

    // Primary path: POST to the Cloudflare Pages Function. Converts to a
    // real email via the configured backend (Web3Forms). Fully mobile-safe.
    try {
      const r = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          inquiryType,
          message,
          _hp: String(fd.get("_hp") || ""),
        }),
      });
      if (r.ok) {
        setIsSubmitted(true);
        return;
      }
    } catch {
      // Fall through to mailto fallback below.
    }

    // Fallback: if the backend is unreachable or not yet configured,
    // hand off to the visitor's mail client so no lead is lost.
    const subject = `[shariqrefai.com] ${inquiryType || "Inquiry"} from ${firstName} ${lastName}`.trim();
    const body = [
      `Name: ${firstName} ${lastName}`,
      `Email: ${email}`,
      `Inquiry Type: ${inquiryType}`,
      "",
      "Message:",
      message,
    ].join("\n");
    const to = emailForInquiry(inquiryType);
    const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setIsSubmitted(true);
  };

  return (
    <SiteLayout>
      <SEO
        title={"Contact Shariq Refai, MD, MBA, Media and Speaking"}
        description="Contact Shariq Refai, MD, MBA, for media interviews, speaking engagements, publishing inquiries, and professional collaborations. Reviewed within three days."
        url="/contact"
        image="/og-contact.jpg"
        schema={[
          personSchema,
          webPageSchema({
            path: "/contact",
            title: "Contact Shariq Refai, MD, MBA, Media and Speaking",
            description:
              "Contact Shariq Refai, MD, MBA, for media interviews, speaking engagements, publishing inquiries, and professional collaborations. Reviewed within three days.",
            type: "ContactPage",
            breadcrumbs: [
              { name: "Home", path: "/" },
              { name: "Contact", path: "/contact" },
            ],
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
          faqSchema([
            {
              question: "Can I contact Dr. Refai about a personal mental health concern?",
              answer:
                "No. shariqrefai.com is an educational author and media platform, not a clinical service. The contact form is not for medical care, treatment, or crisis communication. For clinical care inquiries, visit shrinkmd.com or contact a qualified, licensed clinician of your choosing. If you are in crisis, call or text 988 in the United States, call 911, or go to the nearest emergency room.",
            },
            {
              question: "What inquiries are appropriate for the contact form?",
              answer:
                "Media and press, speaking engagements, publishing, and professional collaborations.",
            },
            {
              question: "Where do contact form submissions go?",
              answer:
                "Inquiries are routed to the appropriate role address (media@, speaking@, partnerships@, or hello@) based on the inquiry type selected. Due to volume, only relevant professional requests may receive a reply.",
            },
            {
              question: "Should I include personal health information in a message?",
              answer:
                "No. Please do not submit confidential medical or mental health information through this website.",
            },
            {
              question: "How long does it take to hear back from Dr. Refai's team?",
              answer:
                "Inquiries are reviewed within three business days. For media inquiries with tight deadlines, please indicate the deadline clearly in your message.",
            },
          ]),
        ]}
      />

      <header className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-5xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Contact" },
            ]}
          />
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight tracking-tight">
            Contact Shariq Refai, MD, MBA
          </h1>
          <p className="contact-intro lede mt-6 text-lg md:text-xl text-secondary font-light leading-relaxed max-w-3xl">
            For media interviews, speaking engagements, publishing inquiries,
            and professional collaborations. Inquiries are reviewed within
            three business days.
          </p>
        </div>
      </header>

      <section className="py-20 px-4 md:px-8 container mx-auto max-w-5xl">
        <div className="grid md:grid-cols-[1fr_1fr] gap-16 items-start">
          
          <div>
            <div className="bg-[#EFECE6] p-8 border-l-4 border-destructive/60 mb-12">
              <h2 className="font-serif text-xl mb-4 text-primary">Important Notice</h2>
              <EducationalDisclaimer variant="boxed" className="mb-4" />
              <p className="text-sm font-light text-secondary leading-relaxed mb-3">
                This contact form is for media, speaking, publishing, and professional inquiries only. It is not intended for medical emergencies, psychiatric treatment, crisis communication, patient-specific questions, or submission of protected health information. For clinical care inquiries, visit <a href={withNetworkUtm("https://shrinkmd.com/", "contact_page")} target="_blank" rel="noopener noreferrer" className="font-medium underline hover:text-primary">shrinkmd.com</a>.
              </p>
              <p className="text-sm font-light text-secondary leading-relaxed mb-3">
                Do not submit confidential medical, psychiatric, or emergency information through this website.
              </p>
              <p className="text-sm font-light text-secondary leading-relaxed mb-4">
                This website is not monitored for clinical or crisis communications.
              </p>
              <p className="text-sm font-semibold text-red-800 leading-relaxed">
                If you are in crisis, call or text 988 in the United States, call 911, or go to the nearest emergency room.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-bold tracking-widest uppercase text-accent mb-2">Location</h3>
                <p className="text-muted-foreground font-light">Jacksonville, Florida</p>
              </div>
              
              <div>
                <h3 className="text-sm font-bold tracking-widest uppercase text-accent mb-2">Email</h3>
                <p className="text-muted-foreground font-light">
                  <a
                    href="mailto:hello@shariqrefai.com"
                    className="hover:text-primary transition-colors underline decoration-accent decoration-2 underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                  >
                    hello@shariqrefai.com
                  </a>
                </p>
              </div>

              <div>
                <h3 className="text-sm font-bold tracking-widest uppercase text-accent mb-2">Inquiry Types</h3>
                <ul className="space-y-2 text-muted-foreground font-light">
                  <li>&bull; Media inquiries & commentary</li>
                  <li>&bull; Speaking engagements</li>
                  <li>&bull; Publishing inquiries</li>
                  <li>&bull; Professional collaborations</li>
                </ul>
              </div>
            </div>
          </div>
          {/* close left column */}

          <div>
            {isSubmitted ? (
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="bg-white border border-border p-12 text-center h-full flex flex-col justify-center min-h-[400px]"
              >
                <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary" aria-hidden="true" focusable="false"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <h3
                  ref={successHeadingRef}
                  tabIndex={-1}
                  className="font-serif text-2xl text-primary mb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                >
                  Thanks, we'll be in touch.
                </h3>
                <p className="text-muted-foreground font-light">
                  Your message has been prepared in your email client and addressed to{" "}
                  <a
                    href="mailto:hello@shariqrefai.com"
                    className="text-accent hover:text-primary transition-colors underline underline-offset-4"
                  >
                    hello@shariqrefai.com
                  </a>
                  . Please send it to complete your inquiry. Due to volume, we
                  may only be able to reply to relevant professional requests.
                </p>
                <button 
                  onClick={() => setIsSubmitted(false)}
                  className="mt-8 text-sm uppercase tracking-widest text-accent hover:text-primary transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
                aria-label="Professional inquiry form"
                noValidate={false}
              >
            {/* Honeypot: real users don't fill this; bots do. Server drops silently. */}
            <input
              type="text"
              name="_hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="sr-only"
              style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
            />
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="firstName" className="text-sm font-medium text-primary">First Name</label>
                    <input 
                      type="text" 
                      id="firstName"
                      name="firstName"
                      autoComplete="given-name"
                      required
                      aria-required="true"
                      className="h-12 px-4 border border-input bg-white text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background focus:border-primary transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="lastName" className="text-sm font-medium text-primary">Last Name</label>
                    <input 
                      type="text" 
                      id="lastName"
                      name="lastName"
                      autoComplete="family-name"
                      required
                      aria-required="true"
                      className="h-12 px-4 border border-input bg-white text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background focus:border-primary transition-all"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-sm font-medium text-primary">Email Address</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    aria-required="true"
                    className="h-12 px-4 border border-input bg-white text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background focus:border-primary transition-all"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="inquiryType" className="text-sm font-medium text-primary">Inquiry Type</label>
                  <select 
                    id="inquiryType"
                    name="inquiryType"
                    required
                    aria-required="true"
                    defaultValue=""
                    className="h-12 px-4 border border-input bg-white text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background focus:border-primary transition-all appearance-none"
                  >
                    <option value="" disabled>Select an option...</option>
                    <option value="media">Media & Press</option>
                    <option value="speaking">Speaking Engagement</option>
                    <option value="publishing">Publishing</option>
                    <option value="collaboration">Professional Collaboration</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="message" className="text-sm font-medium text-primary">Message</label>
                  <textarea 
                    id="message"
                    name="message"
                    required
                    aria-required="true"
                    aria-describedby="message-help"
                    rows={5}
                    className="p-4 border border-input bg-white text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background focus:border-primary transition-all resize-y"
                  ></textarea>
                  <p id="message-help" className="text-xs text-muted-foreground font-light">
                    Please do not include any health information, symptoms, medication details, or emergency information.
                  </p>
                </div>

                <button 
                  type="submit"
                  className="h-12 bg-primary text-primary-foreground font-medium hover:bg-secondary transition-colors mt-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  Submit Inquiry
                </button>
              </form>
            )}
          </div>

        </div>
      </section>

      {/* Response Expectations + What to Include + Clinical Care Redirect + Mailing Address */}
      <section className="container mx-auto max-w-3xl px-4 md:px-8 pb-20 md:pb-24 flex flex-col gap-12">
        <div className="response-expectations">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Response Expectations
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Inquiries submitted through this form are reviewed Monday
            through Friday during business hours. Most inquiries receive an
            initial response within three business days. For
            deadline-sensitive media or speaking requests, please indicate
            the deadline in your message; same-day responses aren't
            guaranteed but are attempted.
          </p>
        </div>

        <div className="what-to-include">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            What to Include in Your Message
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            The clearer your initial message, the faster a response can
            come. Helpful details include:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-secondary font-light leading-relaxed">
            <li>
              <strong className="font-medium text-primary">For media:</strong>{" "}
              publication name, deadline, topic, length, format (interview,
              written contribution, panel).
            </li>
            <li>
              <strong className="font-medium text-primary">For speaking:</strong>{" "}
              event name, date or date range, audience size and type,
              format (keynote, panel, workshop), virtual or in-person,
              location if applicable.
            </li>
            <li>
              <strong className="font-medium text-primary">For publishing:</strong>{" "}
              book title, publisher, format (foreword, blurb, full
              chapter), timeline.
            </li>
            <li>
              <strong className="font-medium text-primary">For collaborations:</strong>{" "}
              organization name, the nature of the collaboration, and any
              timing constraints.
            </li>
          </ul>
        </div>

        <div className="clinical-care-redirect border-l-2 border-accent pl-6">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Looking for Clinical Care?
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-3">
            This form isn't for clinical or patient-care inquiries.
            shariqrefai.com is an educational and editorial platform and
            doesn't provide clinical services. For psychiatric care, please
            visit{" "}
            <a
              href={withNetworkUtm("https://shrinkmd.com/", "contact_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              shrinkmd.com
            </a>
            , the separate telepsychiatry practice Dr. Refai founded.{" "}
            <a
              href={withNetworkUtm("https://shrinkmd.com/start-care", "contact_page_book")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              Book an evaluation at shrinkMD
            </a>
            .
          </p>
          <p className="text-secondary font-light leading-relaxed">
            If you're in crisis, call or text 988 in the United States,
            call 911, or go to your nearest emergency room.
          </p>
        </div>

        <div className="mailing-address">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Mailing Address (Reference Only)
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            For press or speaking correspondence that requires a physical
            address, please contact us first through the form to confirm
            the correct mailing address for the inquiry. shariqrefai.com
            doesn't accept unsolicited physical materials or packages.
          </p>
        </div>
      </section>
    
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="contact_next_step"
            destinations={["shrinkmd", "shrinq", "shrinkopedia"]}
          />
        </div>
      </section>

      {/* IndieAuth / relmeauth verified profile links. Hidden visually but
          discoverable by identity crawlers and IndieAuth agents. */}
      <div className="sr-only" aria-hidden="false">
        <a rel="me" href="https://www.linkedin.com/in/shariqrefai">LinkedIn</a>
        <a rel="me" href="https://www.doximity.com/pub/shariq-refai-md">Doximity</a>
        <a rel="me" href="https://orcid.org/0009-0009-1090-4373">ORCID</a>
        <a rel="me" href="https://www.wikidata.org/wiki/Q139822307">Wikidata</a>
      </div>
          <RelatedPages
        entries={[
          { href: "/media-inquiries", label: "Media & press inquiries form", why: "Structured intake for interview, podcast, and press requests." },
          { href: "/faq", label: "Frequently asked questions", why: "Common questions answered before you write." },
          { href: "/press", label: "Press coverage & media features", why: "Interviews and features that may answer your question." },
        ]}
      />
    </SiteLayout>
  );
}
